// treeclimate_recommender.js
// Minimaler API-Server (Node.js, Express) für gewichtete Baumarten-Empfehlung via OpenAI
// Installation:
//   npm init -y
//   npm install openai express cors dotenv
// Start:
//   OPENAI_API_KEY=dein_key node treeclimate_recommender.js
//   -> POST http://localhost:3000/recommendations

async function openaiChat(model, temperature, max_tokens, messages) {
  const res = await fetch("/api/openai-chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model, temperature, max_tokens, messages }),
  });
  if (!res.ok) throw new Error(`OpenAI-Proxy Fehler: ${res.status}`);
  return res.json();
}

const demoMode = false;

const SYSTEM = `Du bist Baumarten-Experte für globale Standorte.
Antworte ausschließlich als JSON im unten definierten Schema.
Berücksichtige: Klima (Hitze/Trockenheit/Frost), Boden (Art, Feuchte, pH),
Versiegelung, Wurzel- & Kronenraum, Salz/Immissionen, Pflege, Ziel (Schatten/Zier/Biodiv),
und Bebauung Vorgaben falls angegeben. Gib nur Arten, die realistisch verfügbar sind.`;

const SCHEMA = {
  standort: {
    koordinaten: "lat, lon",
    hoehenlage_m: 0,
    klima: { tmean_c: 0.0, hitzetage: 0, frosttage: 0, niederschlag_mm: 0 },
    boden: { art: "", feuchte: "", ph: 7.0, verdichtung: "gering/mittel/hoch" },
    versiegelung_prozent: 0,
    wurzelraum_m3: 0,
    kronenraum_hindernisse: "Text",
    belastungen: {
      streusalz: "gering/mittel/hoch",
      immissionen: "gering/mittel/hoch",
    },
    ziele: {
      funktion: ["Schatten"],
      biodiv: ["Insekten"],
      endgroesse: "klein/mittel/gross",
    },
    vorgaben: { allergenarm: false, fruchtfrei: false },
  },
};

const INSTRUCTIONS = String.raw`
Gib eine Liste von genau 5 Vorschlägen zurück:
[
  {
    "art": "Deutscher Name (Gattung Art)",
    "eignung_score": 0-100,
    "begr": "Eine Standort Bewertung nach Galk sowie die geologische Beschaffenheit mit KlimaWerten die JSON lesbarsind, Struktur und Funktion. (<= 5 Saetze)",
    "anforderungen": {
      "min_wurzelraum_m3": 0,
      "boden_ph": "Spanne",
      "pflegehinweis": "kurz"
    },
    "pflanzqualitaet": "z.B. Hochstamm 16-18 cm, Ballen",
    "hinweise": "optional: Salz/Immission/Wind"
  },
  {...}, {...}
]
Kein Fließtext außerhalb des JSON! Validiere das Json FOrmat bevor du es zurückschickst und passe es ggf an.
`;

function buildMessages(payloadObj, recommendationData) {
  // TOOODOOOOOO recommendationData einbauen, wirft noch fehler oder dauert zu lange bei

  const normalizedRecommendationData = normalisiereDeutsch(recommendationData);

  return [
    { role: "system", content: SYSTEM },
    {
      role: "user",
      content: "SCHEMA:\n" + normalizedRecommendationData,
    },
    { role: "user", content: "STANDORTDATEN:\n" + JSON.stringify(payloadObj) },
    { role: "user", content: INSTRUCTIONS },
  ];
}

// JSON robust aus Text extrahieren
function parseJsonLoose(text) {
  try {
    return JSON.parse(text);
  } catch {}
  const m = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (m) {
    try {
      return JSON.parse(m[1]);
    } catch {}
  }
  try {
    return JSON.parse(text.trim());
  } catch (e) {
    throw new Error(
      "Antwort nicht als JSON parsebar: " + e.message + "\nRAW:\n" + text
    );
  }
}

export async function aiTreeRecommenderService(lat, lon, recommendationData) {
  //Wenn Demo Modus aus ist frage bei ChatGPT Api an
  if (!demoMode) {
    if (typeof lat !== "number" || typeof lon !== "number") {
      throw new Error("lat und lon (Zahlen) sind erforderlich.");
    }

    // Minimaler Payload – bei Bedarf um echte Standortparameter erweitern
    const standortdaten = { standort: { koordinaten: [lat, lon] } };

    const chat = await openaiChat(
      "gpt-4o", 0.2, 1500,
      buildMessages(standortdaten, recommendationData)
    );

    const text = chat.choices?.[0]?.message?.content ?? "";
    const data = parseJsonLoose(text);

    if (!Array.isArray(data) || data.length !== 5) {
      throw new Error("Unerwartetes Format (nicht genau 3 Vorschläge).");
    }

    return data;
  }
  // Wenn DemoModus aktiv ist gebe DemoJson zurück
  else {
    await waitRandom();
    return [
      {
        artLatein: "Quercus robur 'Fastigiata'",
        artDeutsch: "Stieleiche 'Fastigiata'",
        eignungScore: 84,
        begr: "Robuste heimische Eiche mit guter Schattenspende und hoher Biodiversitätsleistung. Verträgt neutrale Böden und ist klimaresilient gegenüber Hitze und Frost.",
        anforderungen: {
          minWurzelraumM3: 30,
          bodenPh: "5.5–7.8",
          pflegehinweis:
            "Jungbäume regelmäßig wässern und frühzeitig erziehen, um stabile Kronenstruktur zu fördern.",
        },
        pflanzqualitaet: "Hochstamm 18–20 cm, Ballen",
        hinweise:
          "Sehr windfest, mäßig salzempfindlich, hohe Immissionstoleranz.",
      },
      {
        artLatein: "Tilia tomentosa 'Brabant'",
        artDeutsch: "Silber-Linde 'Brabant'",
        eignungScore: 88,
        begr: "Sehr schattenspendend und trockenheitstolerant, zudem wertvoll für Bienen. Geeignet für städtische Bedingungen und neutrale Böden.",
        anforderungen: {
          minWurzelraumM3: 25,
          bodenPh: "6.0–8.0",
          pflegehinweis:
            "In den ersten Standjahren bewässern; Kronenpflege für symmetrischen Aufbau.",
        },
        pflanzqualitaet: "Hochstamm 18–20 cm, Ballen",
        hinweise: "Relativ salztolerant, gute Immissionstoleranz.",
      },
      {
        artLatein: "Acer platanoides 'Deborah'",
        artDeutsch: "Spitzahorn 'Deborah'",
        eignungScore: 80,
        begr: "Kräftige, schattenspendende Krone mit attraktivem Laub. Tolerant gegenüber urbanen Bedingungen und wertvoll für Insekten.",
        anforderungen: {
          minWurzelraumM3: 20,
          bodenPh: "6.0–7.8",
          pflegehinweis:
            "Regelmäßig wässern in Trockenperioden, Aufbauschnitt für stabile Krone.",
        },
        pflanzqualitaet: "Hochstamm 16–18 cm, Ballen",
        hinweise: "Mäßige Salzverträglichkeit, toleriert Luftschadstoffe.",
      },
    ];
  }
}

// Hilfsfunktion, die eine zufällige Zeit wartet
function waitRandom() {
  const ms = Math.floor(Math.random() * (6000 - 1000 + 1)) + 1000; // 1000–6000 ms
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Verdichtet & lokalisiert das Objekt:
 *  - Deutsche Schlüssel
 *  - Einheiten direkt am Wert (als String)
 *  - Entfernt null/leer
 *  - Periode zusätzlich als Monat & Jahr
 */
function normalisiereDeutsch(input, opts = {}) {
  const decimals = opts.decimals ?? 2;
  const latlonDecimals = opts.latlonDecimals ?? 6;
  const percentDecimals = opts.percentDecimals ?? 2;

  const r = (x, d = decimals) =>
    typeof x === "number" && Number.isFinite(x) ? Number(x.toFixed(d)) : x;

  const attach = (val, unit, { dec = decimals } = {}) => {
    if (val === undefined || val === null) return undefined;
    if (unit === "ratio") {
      const pct = Number((Number(val) * 100).toFixed(percentDecimals));
      return `${pct} %`;
    }
    if (unit === "days") return `${val} Tage`;
    if (unit && unit !== "-") return `${r(val, dec)} ${unit}`;
    // Kein Unit -> als String zurückgeben
    return String(r(val, dec));
  };

  const parseDepthRange = (s) => {
    const m = /(\d+)\s*-\s*(\d+)\s*cm/i.exec(s || "");
    return m ? [Number(m[1]), Number(m[2])] : null;
  };

  const byDepth = (arr = []) =>
    Object.fromEntries(arr.filter(Boolean).map((e) => [e.depth, e.value]));

  const prune = (obj) => {
    if (Array.isArray(obj)) {
      const a = obj
        .map(prune)
        .filter(
          (v) =>
            v !== undefined &&
            v !== null &&
            (typeof v !== "object" || Object.keys(v).length)
        );
      return a.length ? a : undefined;
    }
    if (obj && typeof obj === "object") {
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        const pv = prune(v);
        if (
          pv !== undefined &&
          pv !== null &&
          (typeof pv !== "object" || Object.keys(pv).length)
        )
          out[k] = pv;
      }
      return Object.keys(out).length ? out : undefined;
    }
    return obj;
  };

  // --- Rohdaten sammeln
  const texture = input.Bodenart || {};
  const water = input["Wasserhaltevermögen"] || {};
  const phArr = input["pH-Wert"]?.ph || [];

  const sandMap = byDepth(texture.sand);
  const siltMap = byDepth(texture.silt);
  const clayMap = byDepth(texture.clay);
  const phMap = byDepth(phArr);
  const pawcMap = byDepth(water.pawc);
  const wv0033Map = byDepth(water.wv0033);
  const wv1500Map = byDepth(water.wv1500);

  const depthSet = new Set(
    [
      ...Object.keys(sandMap || {}),
      ...Object.keys(siltMap || {}),
      ...Object.keys(clayMap || {}),
      ...Object.keys(phMap || {}),
      ...Object.keys(pawcMap || {}),
      ...Object.keys(wv0033Map || {}),
      ...Object.keys(wv1500Map || {}),
    ].filter(Boolean)
  );

  const depthList = Array.from(depthSet).sort((a, b) => {
    const pa = parseDepthRange(a)?.[0] ?? 0;
    const pb = parseDepthRange(b)?.[0] ?? 0;
    return pa - pb;
  });

  const textureUnit = texture.unit || "%";
  const waterUnit = water.unit || "% Volumenwasser";

  const schichten = depthList
    .map((dstr) => {
      const parsed = parseDepthRange(dstr);
      const tiefe = parsed ? `${parsed[0]}–${parsed[1]} cm` : dstr;

      const layer = {
        tiefe, // z. B. "30–60 cm"
        bodenart_pct: prune({
          sand: attach(sandMap[dstr], textureUnit),
          schluff: attach(siltMap[dstr], textureUnit),
          ton: attach(clayMap[dstr], textureUnit),
        }),
        "pH-Wert": attach(phMap[dstr], "-"),
        wasserhaltevermögen_pct: prune({
          pawc: attach(pawcMap[dstr], waterUnit),
          wv0033: attach(wv0033Map[dstr], waterUnit),
          wv1500: attach(wv1500Map[dstr], waterUnit),
        }),
      };
      return prune(layer);
    })
    .filter(Boolean);

  // Periode + Monat/Jahr
  const start = input.period?.start_date;
  const ende = input.period?.end_date;
  const tage = input.period?.valid_days;
  const monate =
    start && ende
      ? { start: start.slice(0, 7), ende: ende.slice(0, 7) }
      : undefined;
  const jahre =
    start && ende
      ? { start: Number(start.slice(0, 4)), ende: Number(ende.slice(0, 4)) }
      : undefined;

  const result = prune({
    periode: prune({
      start,
      ende,
      tage,
      monate,
      jahre,
    }),
    ort: prune({
      breite: attach(r(input.location?.latitude, latlonDecimals), "°", {
        dec: latlonDecimals,
      }),
      länge: attach(r(input.location?.longitude, latlonDecimals), "°", {
        dec: latlonDecimals,
      }),
      zeitzone: input.location?.timezone,
    }),
    boden: schichten.length === 1 ? schichten[0] : { schichten },
    klima_mittel: prune({
      niederschlag_pro_tag: attach(input.means?.precipitation_mm_per_day, "mm"),
      kurzwellige_strahlung_pro_tag: attach(
        input.means?.shortwave_radiation_MJ_m2_day,
        "MJ/m²"
      ),
      bodentemp_0_7cm: attach(input.means?.soil_temp_0_7cm_C, "°C"),
      tmin_2m: attach(input.means?.t2m_min_C, "°C"),
      wind_10m_max: attach(input.means?.windspeed10m_max_m_s, "m/s"),
      boeen_10m_max: attach(input.means?.windgusts10m_max_m_s, "m/s"),
    }),
    klima_summen: prune({
      niederschlag_gesamt: attach(
        input.aggregates?.precipitation_total_mm,
        "mm"
      ),
      frosttage: attach(input.aggregates?.frost_days, "days"),
      frosttage_anteil: attach(input.aggregates?.frost_days_share, "ratio"),
    }),
    meta: prune({
      verdichtung: input.Verdichtung,
      bodenbelag: input.Bodenbelag,
      nutzung: input.Nutzung,
      // source/url werden bewusst weggelassen
    }),
  });

  return result;
}
